/**
 * Access-control feature catalog. Single source of truth shared between the API
 * (enforcement + seeding) and the web app (nav gating + admin editor).
 *
 * A "feature" is a navigable area of the OS that a user can be granted or denied
 * access to. The OS is organised by department: Sales, CSM, GTM, Marketing and Finance
 * each get a top-level area, plus the cross-cutting tools (James, Automations) and
 * admin (Account). Granularity is top-level areas + one sub-level, and only where
 * it matters: Sales splits into AEs / SDRs and Marketing splits into its surfaces
 * (so an admin can limit someone to e.g. just AI Visibility). The tree shape means
 * deeper splits can be added later without changing the model.
 *
 * Effective access for a user is computed as:
 *   effective(feature) = userOverride(feature) ?? roleDefault(role, feature)
 * checked per-node (no implicit parent->child cascade). Admins always have full
 * access, EXCEPT `adminOptIn` features, which are hidden for everyone by default
 * and only appear for an admin who explicitly enabled them in their Settings
 * (stored as a per-user 'allow' override). `alwaysAllowed` features (Settings)
 * are never gated.
 *
 * NOTE: this catalog defines WHICH features exist. The per-role defaults below
 * are only the initial seed — the running source of truth lives in the DB
 * (role_feature_defaults) and is editable by admins at runtime.
 */

/** User roles. Mirrors the `user_role` enum in the API db schema. */
export type Role = 'admin' | 'member' | 'viewer';

export const ROLES: readonly Role[] = ['admin', 'member', 'viewer'];

export interface FeatureNode {
  /** Stable dot-notation id, e.g. `dashboard.cs`. Used as the DB key. */
  id: string;
  /** Human label for the admin editor. */
  label: string;
  /** Parent feature id, for sub-areas. */
  parentId?: string;
  /** Route this feature gates in the web app, when it maps to a nav entry. */
  navPath?: string;
  /** When true the feature is never gated (e.g. a user's own Settings). */
  alwaysAllowed?: boolean;
  /**
   * When true the feature is hidden by default for EVERYONE, including admins.
   * An admin only sees it after enabling it for themselves in Settings
   * (persisted as a per-user 'allow' override).
   */
  adminOptIn?: boolean;
}

/**
 * Flat list of every feature. Parents appear before their children.
 * `as const` gives us literal `id`s for the FeatureId union.
 */
export const FEATURES = [
  { id: 'home', label: 'Home', navPath: '/' },

  { id: 'sales', label: 'Sales', navPath: '/sales' },
  { id: 'sales.ae', label: 'Account Executives', parentId: 'sales' },
  { id: 'sales.sdr', label: 'SDRs', parentId: 'sales' },
  { id: 'sales.research', label: 'Deep Research', parentId: 'sales' },
  { id: 'sales.bookings', label: 'Demo bookings', parentId: 'sales' },
  // Per-rep outbound activity. `adminOptIn` keeps it visible to Walter only for
  // now — the numbers are meant for coaching, and they need a few weeks of clean
  // data before the team sees them. To open it up, drop the flag and add the id
  // to VIEWER_MEMBER_DEFAULTS.
  { id: 'sales.outbound', label: 'Outbound activity', parentId: 'sales', adminOptIn: true },
  // Per-signal outreach funnel, from lemlist and the GTM plays queue. Hidden for
  // now, like Outbound activity: the signal campaigns have no leads yet and the
  // numbers need a few weeks of clean data before the team sees them. To open it
  // up, drop the flag and add the id to VIEWER_MEMBER_DEFAULTS.
  { id: 'sales.outreach', label: 'Outreach', parentId: 'sales', adminOptIn: true },
  // Per-account outreach pilots: who is assigned to which rep and where each
  // person stands in lemlist. Admins see it by default; the reps get it through
  // the Sales team so they can follow their accounts and tick calls.
  { id: 'sales.pilots', label: 'Outreach Buckets', parentId: 'sales' },

  { id: 'csm', label: 'CSM', navPath: '/csm' },

  // GTM — Signal Ops. Everything before the demo: the canonical record, the
  // signal ledger, account scoring, weighted attribution and the target list.
  // `adminOptIn` on the department and every child for now: the signal weights
  // are explicit first guesses and the identity matcher wants a few passes of
  // review before anyone makes a call on its output. To open it up, drop these
  // flags and add the ids to VIEWER_MEMBER_DEFAULTS.
  { id: 'gtm', label: 'GTM', navPath: '/gtm', adminOptIn: true },
  { id: 'gtm.accounts', label: 'GTM — Hot accounts', parentId: 'gtm', adminOptIn: true },
  { id: 'gtm.channels', label: 'GTM — Channels', parentId: 'gtm', adminOptIn: true },
  { id: 'gtm.model', label: 'GTM — Model', parentId: 'gtm', adminOptIn: true },
  { id: 'gtm.matches', label: 'GTM — Match review', parentId: 'gtm', adminOptIn: true },
  { id: 'gtm.health', label: 'GTM — Data health', parentId: 'gtm', adminOptIn: true },
  { id: 'gtm.sourcing', label: 'GTM — Sourcing', parentId: 'gtm', adminOptIn: true },
  { id: 'gtm.plays', label: 'GTM — Plays', parentId: 'gtm', adminOptIn: true },

  { id: 'marketing', label: 'Marketing', navPath: '/marketing' },
  { id: 'marketing.visibility', label: 'AI Visibility', parentId: 'marketing', adminOptIn: true },
  { id: 'marketing.search-visibility', label: 'Search Visibility', parentId: 'marketing' },
  { id: 'marketing.gtm', label: 'GTM', parentId: 'marketing' },
  { id: 'marketing.content', label: 'Content Engine', parentId: 'marketing' },
  { id: 'marketing.linkedin', label: 'LinkedIn Posts', parentId: 'marketing' },
  { id: 'marketing.website-visits', label: 'Website visits', parentId: 'marketing' },
  { id: 'marketing.web-analytics', label: 'Web analytics', parentId: 'marketing' },
  { id: 'marketing.visuals', label: 'Visuals', parentId: 'marketing' },

  { id: 'finance', label: 'Finance', navPath: '/finance' },
  { id: 'finance.customers', label: 'Customers', parentId: 'finance' },

  // Personal organizer. Every node is `adminOptIn` on purpose, children included:
  // `canAccessArea` is "self OR any child", so leaving a child un-gated would put
  // the nav item back in front of every admin. Opting in is per-node, per-user,
  // which is exactly the "only visible to me for now" rollout we want. To open it
  // up later, drop these flags and add the ids to VIEWER_MEMBER_DEFAULTS.
  // The parent grants the board itself — there is deliberately no separate
  // `organizer.board` node, or enabling only "Organizer" would leave every tab
  // inaccessible and bounce the user off the page.
  { id: 'organizer', label: 'Organizer', navPath: '/organizer', adminOptIn: true },
  { id: 'organizer.calendar', label: 'Organizer — Calendar', parentId: 'organizer', adminOptIn: true },
  { id: 'organizer.inbox', label: 'Organizer — Review Inbox', parentId: 'organizer', adminOptIn: true },
  { id: 'organizer.reminders', label: 'Organizer — Reminders', parentId: 'organizer', adminOptIn: true },

  // Company Admin — company-wide documents that belong to no department:
  // handbook, policies, compliance, shared tooling. Deliberately has no
  // `navPath`: it exists so links have somewhere to be filed, not as a page of
  // its own. Everyone gets it.
  { id: 'company', label: 'Company Admin' },

  // The link register. Everyone gets the page; each link carries the feature id
  // that gates it, so a person only ever sees the sections they can already
  // reach elsewhere in the OS. Granting `resources` alone shows an empty page.
  { id: 'resources', label: 'Resources', navPath: '/resources' },
  // Read a department's links without opening its tab: `resources.sales` shows
  // the Sales section of the register to someone who has no Sales area. Only
  // for viewing; filing a link there still needs the area itself.
  { id: 'resources.sales', label: 'Sales links', parentId: 'resources' },
  { id: 'resources.marketing', label: 'Marketing links', parentId: 'resources' },

  // On-brand documents: drop an off-brand document in, get it back on the
  // SEQUESTO document system, checked page by page. Members get it by default
  // (it exists for the team, not for admins); viewers do not, since every run
  // spends several model passes.
  { id: 'documents', label: 'On-brand documents', navPath: '/documents' },

  { id: 'james', label: 'James', navPath: '/james' },
  { id: 'automations', label: 'Automations', navPath: '/automations' },

  { id: 'integrations', label: 'Integrations', navPath: '/integrations' },
  { id: 'signatures', label: 'Signatures', navPath: '/signatures' },
  { id: 'users', label: 'Users', navPath: '/users' },
  { id: 'settings', label: 'Settings', navPath: '/settings', alwaysAllowed: true },
] as const satisfies readonly FeatureNode[];

export type FeatureId = (typeof FEATURES)[number]['id'];

/** Typed view of the catalog for reading optional props (`as const` narrows them away). */
const FEATURE_NODES: readonly FeatureNode[] = FEATURES;

/** All feature ids, in catalog order. */
export const FEATURE_IDS: readonly FeatureId[] = FEATURES.map((f) => f.id);

/** Children of a feature, in catalog order. */
export function childrenOf(id: FeatureId): FeatureNode[] {
  return FEATURE_NODES.filter((f) => f.parentId === id);
}

/** Top-level features (no parent). */
export function topLevelFeatures(): FeatureNode[] {
  return FEATURE_NODES.filter((f) => !f.parentId);
}

export function isFeatureId(value: unknown): value is FeatureId {
  return typeof value === 'string' && FEATURE_IDS.includes(value as FeatureId);
}

/** Features that are never gated and should always be available. */
export const ALWAYS_ALLOWED_FEATURES: readonly FeatureId[] = FEATURE_NODES.filter(
  (f) => f.alwaysAllowed
).map((f) => f.id as FeatureId);

/** Features hidden by default for everyone (admins included) until opted into via Settings. */
export const ADMIN_OPT_IN_FEATURES: readonly FeatureId[] = FEATURE_NODES.filter(
  (f) => f.adminOptIn
).map((f) => f.id as FeatureId);

export function isAdminOptInFeature(feature: FeatureId): boolean {
  return ADMIN_OPT_IN_FEATURES.includes(feature);
}

/**
 * Initial per-role default access, used ONLY to seed the DB the first time.
 * Viewers and members default to Home + CSM + Marketing (no sub-areas) + James
 * + Resources (whose contents filter down to the sections they can reach);
 * admins get everything except opt-in features (AI Visibility), which nobody
 * sees by default. Always-allowed features are included for completeness.
 */
const VIEWER_MEMBER_DEFAULTS: FeatureId[] = [
  'home',
  'csm',
  'marketing',
  'james',
  'resources',
  'company',
  ...ALWAYS_ALLOWED_FEATURES,
];

export const DEFAULT_ROLE_FEATURES: Record<Role, FeatureId[]> = {
  admin: FEATURE_IDS.filter((id) => !isAdminOptInFeature(id)),
  member: [...VIEWER_MEMBER_DEFAULTS, 'documents'],
  viewer: [...VIEWER_MEMBER_DEFAULTS],
};
